import { useEffect, useRef, useState } from "react";
import { api, getToken, setToken, type UserOut } from "./api";
import { Login } from "./pages/Login";
import { Overview } from "./pages/Overview";
import { Workshops } from "./pages/Workshops";
import { WorkshopDetail } from "./pages/WorkshopDetail";
import { Codes } from "./pages/Codes";
import { Analytics } from "./pages/Analytics";
import { Tickets } from "./pages/Tickets";
import { Leads } from "./pages/Leads";
import { Haydovchilar } from "./pages/Haydovchilar";
import { AppVersion } from "./pages/AppVersion";
import { Sidebar } from "./components/Sidebar";
import { useHashRoute, type Location, type Route } from "./routing";

const PAGE_TITLE: Record<Route, string> = {
  "overview": "Bosh sahifa",
  "workshops": "Ustaxonalar",
  "workshop-detail": "Ustaxona",
  "codes": "Aktivatsiya kodlari",
  "analytics": "Analitika",
  "tickets": "Murojaatlar",
  "leads": "Leadlar",
  "haydovchilar": "Haydovchilar",
  "app-version": "Ilova versiyasi",
};

export function App() {
  const logoutPending = useRef(false);
  const [logoutError, setLogoutError] = useState(false);
  const [user, setUser] = useState<UserOut | null>(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [loc, navigate] = useHashRoute();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAddWorkshop, setShowAddWorkshop] = useState(false);
  const [showAddCode, setShowAddCode] = useState(false);

  useEffect(() => {
    (async () => {
      if (!getToken()) { setBootLoading(false); return; }
      try {
        const me = await api.me();
        if (me.role !== "platform_owner") { setToken(null); }
        else setUser(me);
      } catch { setToken(null); }
      finally { setBootLoading(false); }
    })();
  }, []);

  useEffect(() => { setMenuOpen(false); }, [loc]);

  async function logout() {
    if (!user || logoutPending.current) return;
    logoutPending.current = true;
    setLogoutError(false);
    try {
      // Environment-admin tokens are stateless and have no database session.
      if (user.id !== 0) await api.logout();
      setToken(null);
      setUser(null);
    } catch {
      setLogoutError(true);
    } finally {
      logoutPending.current = false;
    }
  }

  if (bootLoading) return <div className="empty">Yuklanmoqda…</div>;
  if (!user) return <Login onLogin={setUser} />;

  function go(next: Location | Route) {
    if (typeof next === "string") {
      if (next === "workshop-detail") return;
      navigate({ route: next } as Location);
    } else navigate(next);
  }

  return (
    <div className={`layout${menuOpen ? " menu-open" : ""}`}>
      <Sidebar
        current={loc.route}
        userName={user.full_name}
        onNavigate={(r) => go(r)}
        onLogout={logout}
      />
      <div className="sidebar-backdrop" onClick={() => setMenuOpen(false)} />
      <div className="main">
        <div className="topbar">
          <button className="hamburger" onClick={() => setMenuOpen((x) => !x)} aria-label="Menyu">
            <span /><span /><span />
          </button>
          <div className="topbar-title">{PAGE_TITLE[loc.route]}</div>
          <span className="topbar-brand">AvtoDaftar</span>
        </div>
        <div className="main-inner">
          {logoutError && (
            <div className="error" role="alert">
              Chiqish tasdiqlanmadi. Internet aloqasini tekshirib, qayta urinib ko'ring.
            </div>
          )}
          {loc.route === "overview" && (
            <Overview
              onNavigate={navigate}
              onOpenCreate={() => { navigate({ route: "workshops" }); setShowAddWorkshop(true); }}
              onOpenNewCode={() => { navigate({ route: "codes" }); setShowAddCode(true); }}
            />
          )}
          {loc.route === "workshops" && (
            <Workshops
              onNavigate={navigate}
              showAdd={showAddWorkshop}
              setShowAdd={setShowAddWorkshop}
            />
          )}
          {loc.route === "workshop-detail" && (
            <WorkshopDetail id={loc.id} onNavigate={navigate} />
          )}
          {loc.route === "codes" && (
            <Codes openCreate={showAddCode} setOpenCreate={setShowAddCode} />
          )}
          {loc.route === "analytics" && <Analytics />}
          {loc.route === "tickets" && <Tickets />}
          {loc.route === "leads" && <Leads />}
          {loc.route === "haydovchilar" && <Haydovchilar />}
          {loc.route === "app-version" && <AppVersion />}
        </div>
      </div>
    </div>
  );
}
